import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useAuth, hasRole } from "@/contexts/AuthContext";
import StatusBadge, { formatCurrency } from "@/components/StatusBadge";
import { toast } from "sonner";
import { Plus, DollarSign, Pencil, Trash2, Printer, ListPlus, History } from "lucide-react";
import { useNavigate } from "react-router-dom";
import LineItemsEditor from "@/components/LineItemsEditor";
import DuplicateInvoicesBanner from "@/components/DuplicateInvoicesBanner";
import { usePersistentSort } from "@/lib/usePersistentSort";

const CURRENCIES = ["USD", "CAD", "GBP", "EUR", "INR", "AUD", "AED", "SAR"];

/**
 * Small strip next to the "New Invoice" button that previews the very next
 * India + LLC invoice numbers so admins know what will be issued.
 */
function SeriesPreviewStrip() {
  const [preview, setPreview] = React.useState({ india: null, llc: null });
  const load = React.useCallback(() => {
    const today = new Date().toISOString().slice(0, 10);
    Promise.all([
      api.get(`/api/invoice-series/next-number?entity=india&date=${today}`).then((r) => r.data).catch(() => null),
      api.get(`/api/invoice-series/next-number?entity=llc&date=${today}`).then((r) => r.data).catch(() => null),
    ]).then(([india, llc]) => setPreview({ india, llc }));
  }, []);
  React.useEffect(() => {
    load();
    const t = setInterval(load, 15000); // refresh every 15s so numbers stay in sync after other users create
    const bump = () => load();
    window.addEventListener("invoice-series:refresh", bump);
    return () => { clearInterval(t); window.removeEventListener("invoice-series:refresh", bump); };
  }, [load]);
  return (
    <div className="hidden md:flex items-center gap-3 text-xs" data-testid="series-preview-strip">
      <div className="border border-slate-200 bg-white rounded-md px-3 py-2 leading-tight">
        <div className="text-[10px] uppercase tracking-wider text-slate-500">Next India</div>
        <div className="font-mono font-semibold text-brand-dark">
          {preview.india?.invoice_number || "—"}
        </div>
      </div>
      <div className="border border-slate-200 bg-white rounded-md px-3 py-2 leading-tight">
        <div className="text-[10px] uppercase tracking-wider text-slate-500">Next LLC</div>
        <div className="font-mono font-semibold text-brand-dark">
          {preview.llc?.invoice_number || "—"}
        </div>
      </div>
    </div>
  );
}

export default function Invoices() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [payOpen, setPayOpen] = useState(null);
  const [lineItemsFor, setLineItemsFor] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const [history, setHistory] = useState(null);
  const [dupeWarning, setDupeWarning] = useState(null);
  // Pending stages for the currently-selected project (used by "Link to
  // pending stage" dropdown so a newly-issued invoice can auto-clear the row).
  const [pendingStagesForProject, setPendingStagesForProject] = useState([]);
  // Sort state: which column + direction. Newest invoice_date first by default.
  // Persisted per user in localStorage so their preference survives reloads.
  const [sortBy, setSortBy] = usePersistentSort("invoices", { key: "invoice_date", dir: "desc" });
  const toggleSort = (key) => setSortBy((prev) => prev.key === key
    ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
    : { key, dir: key === "invoice_date" || key === "due_date" || key === "invoice_number" ? "desc" : "asc" }
  );
  const openHistory = async (inv) => {
    setHistoryFor(inv); setHistory(null);
    try {
      const { data } = await api.get(`/api/invoices/${inv.id}/payment-history`);
      setHistory(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load history"); }
  };
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [clientFilter, setClientFilter] = useState("all");
  const [projectFilter, setProjectFilter] = useState("all");
  const [entityFilter, setEntityFilter] = useState("all");
  const [dueBefore, setDueBefore] = useState("");
  const emptyForm = {
    client_id: "", project_id: "", payment_stage_id: "",
    invoice_number: "", invoice_date: new Date().toISOString().slice(0, 10),
    due_date: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
    invoice_amount: 0, tax_amount: 0, currency: "USD", status: "sent", notes: "",
    entity_type: "", invoice_for_label: "", invoice_for_value: "", reference: "",
    line_description: "", hsn_sac_code: "", quantity: 1, rate: 0,
    promotional_discount: 0, round_off: 0, usd_to_inr_note: "",
    bill_to_name: "", bill_to_address: "", bill_to_contact: "", bill_to_phone: "", bill_to_email: "", bill_to_gstin: "",
    // Multi-line-item picker. Empty by default; when populated, the backend
    // ignores the legacy single-line fields above.
    line_items: [],
  };
  const [form, setForm] = useState(emptyForm);
  const [pay, setPay] = useState({ amount_received: 0, payment_date: new Date().toISOString().slice(0, 10), payment_method: "wire", reference: "", notes: "", amount_received_inr: "", fx_rate: "" });
  // Prior payments on the invoice currently being paid — used to suggest a
  // rate chip (last conversion rate the user actually used on this invoice).
  const [priorTxns, setPriorTxns] = useState([]);
  // Auto-prefill invoice_number from the admin-defined series whenever the
  // Create dialog is open and its entity/date change. Admin can override the
  // suggestion by typing over it.
  const [autoNumberPrefilled, setAutoNumberPrefilled] = useState(false);
  useEffect(() => {
    if (!open) { setAutoNumberPrefilled(false); return; }
    // If admin has typed a custom number, don't overwrite it.
    if (form.invoice_number && !autoNumberPrefilled) return;
    const entity = form.entity_type || (form.currency === "INR" ? "india" : "llc");
    if (!entity) return;
    let cancelled = false;
    api.get(`/api/invoice-series/next-number?entity=${entity}&date=${form.invoice_date}`)
      .then((r) => {
        if (cancelled) return;
        setForm((prev) => ({ ...prev, invoice_number: r.data.invoice_number }));
        setAutoNumberPrefilled(true);
      })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form.entity_type, form.currency, form.invoice_date]);

  useEffect(() => {
    if (!payOpen?.id) { setPriorTxns([]); return; }
    let cancelled = false;
    api.get(`/api/invoices/${payOpen.id}`)
      .then((r) => { if (!cancelled) setPriorTxns(r.data?.transactions || []); })
      .catch(() => { if (!cancelled) setPriorTxns([]); });
    return () => { cancelled = true; };
  }, [payOpen?.id]);
  // Compute up to 2 unique historical rates from prior payments (most recent first).
  // Values outside the sane 0.01–500 band are treated as data-entry outliers and
  // filtered out so we never suggest a nonsense rate to the accountant.
  const rateHistory = useMemo(() => {
    const seen = new Set();
    const list = [];
    for (const t of priorTxns) {
      if (!t.amount_received || !t.amount_received_inr) continue;
      if (t.currency && t.currency !== (payOpen?.currency || "")) continue;
      const rate = Number(t.amount_received_inr) / Number(t.amount_received);
      if (!Number.isFinite(rate) || rate < 0.01 || rate > 500) continue;
      const key = rate.toFixed(4);
      if (seen.has(key)) continue;
      seen.add(key);
      list.push({ rate: Number(rate.toFixed(6)), date: (t.payment_date || t.created_at || "").slice(0, 10) });
      if (list.length >= 2) break;
    }
    return list;
  }, [priorTxns, payOpen?.currency]);
  // Track which of {fx_rate, amount_received_inr} the user edited LAST so we
  // never overwrite the field they're actively typing into (avoids a caret-
  // stealing feedback loop when both are linked bi-directionally).
  const [payLastFx, setPayLastFx] = useState(null);   // "rate" | "inr" | null
  // Bi-directional linkage:
  //   amount × rate = inr   ⇔   rate = inr / amount
  // Whichever field the user edited last is preserved; the OTHER is recomputed.
  const setPayAmount = (v) => {
    setPay((p) => {
      const next = { ...p, amount_received: v };
      const amt = Number(v);
      if (Number.isFinite(amt) && amt > 0) {
        if (payLastFx === "rate" && p.fx_rate !== "" && p.fx_rate !== null) {
          next.amount_received_inr = String(Number((amt * Number(p.fx_rate)).toFixed(2)));
        } else if (payLastFx === "inr" && p.amount_received_inr !== "" && p.amount_received_inr !== null) {
          next.fx_rate = String(Number((Number(p.amount_received_inr) / amt).toFixed(6)));
        }
      }
      return next;
    });
  };
  const setPayRate = (v) => {
    setPayLastFx("rate");
    setPay((p) => {
      const next = { ...p, fx_rate: v };
      const amt = Number(p.amount_received);
      const rate = Number(v);
      if (Number.isFinite(amt) && amt > 0 && Number.isFinite(rate) && v !== "") {
        next.amount_received_inr = String(Number((amt * rate).toFixed(2)));
      }
      return next;
    });
  };
  const setPayInr = (v) => {
    setPayLastFx("inr");
    setPay((p) => {
      const next = { ...p, amount_received_inr: v };
      const amt = Number(p.amount_received);
      const inr = Number(v);
      if (Number.isFinite(amt) && amt > 0 && Number.isFinite(inr) && v !== "") {
        next.fx_rate = String(Number((inr / amt).toFixed(6)));
      }
      return next;
    });
  };

  const load = async () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (statusFilter !== "all") params.set("payment_status", statusFilter);
    const { data } = await api.get(`/api/invoices?${params}`);
    setItems(data);
  };
  useEffect(() => { load(); }, [q, statusFilter]);
  useEffect(() => { api.get("/api/projects").then((r) => setProjects(r.data)); }, []);
  useEffect(() => { api.get("/api/clients").then((r) => setClients(r.data)); }, []);

  // Fetch this project's still-pending stages (so admin can attach the invoice
  // to a specific stage at creation time; opting out is fine — leave blank).
  useEffect(() => {
    if (!form.project_id || !open) { setPendingStagesForProject([]); return; }
    let cancelled = false;
    api.get("/api/payment-stages/pending")
      .then((r) => {
        if (cancelled) return;
        const list = (r.data || [])
          .filter((it) => it.project?.id === form.project_id)
          .map((it) => it.stage);
        setPendingStagesForProject(list);
      })
      .catch(() => { if (!cancelled) setPendingStagesForProject([]); });
    return () => { cancelled = true; };
  }, [form.project_id, open]);

  const chosenProject = projects.find((p) => p.id === form.project_id);

  // Hoisted — otherwise these array manipulations re-run on every keystroke.
  const clientById = useMemo(() => {
    const m = new Map(); clients.forEach((c) => m.set(c.id, c)); return m;
  }, [clients]);
  const projectsForForm = useMemo(
    () => projects.filter((p) => !form.client_id || p.client_id === form.client_id),
    [projects, form.client_id]
  );
  const projectsForFilter = useMemo(
    () => projects.filter((p) => clientFilter === "all" || p.client_id === clientFilter).slice(0, 200),
    [projects, clientFilter]
  );
  const visibleInvoices = useMemo(() => (
    items.filter((i) => {
      if (clientFilter !== "all" && i.client_id !== clientFilter) return false;
      if (projectFilter !== "all" && i.project_id !== projectFilter) return false;
      if (entityFilter !== "all" && (i.entity_type || "").toLowerCase() !== entityFilter) return false;
      if (dueBefore && i.due_date && i.due_date.slice(0, 10) > dueBefore) return false;
      return true;
    })
  ), [items, clientFilter, projectFilter, entityFilter, dueBefore]);

  /** After a successful India-entity create/update, silently grab the fresh
   *  FBIL screenshot and trigger the browser to download it — audit trail
   *  lives in the user's Downloads folder, not the server. */
  const autoDownloadFbilScreenshot = async (invoiceId, invoiceNumber, entityType, currency) => {
    if ((entityType || "").toLowerCase() !== "india") return;
    if ((currency || "USD").toUpperCase() === "INR") return;
    try {
      const res = await api.get(`/api/invoices/${invoiceId}/fbil-screenshot`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "image/png" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, "");
      a.href = url;
      a.download = `FBIL_${(currency || "USD")}_INR_${(invoiceNumber || invoiceId).replace(/\//g, "-")}_${stamp}.png`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      toast.info("FBIL rate screenshot downloaded for audit");
    } catch (err) {
      // Non-fatal — the invoice is already saved. Just tell the user.
      toast.error("Invoice saved, but FBIL screenshot capture failed. Retry from the invoice detail page.");
    }
  };

  const create = async () => {
    if (!form.client_id) return toast.error("Client is required");
    if (!form.project_id) return toast.error("Project is required");
    try {
      const items = (form.line_items || []).filter((li) => (li.description || "").trim() || Number(li.amount || li.rate || 0) > 0);
      const total = items.length > 0
        ? items.reduce((s, li) => s + Number(li.amount || li.rate || 0), 0)
        : Number(form.invoice_amount);
      const body = {
        ...form,
        // Empty invoice_number means "use next auto-generated one from the admin series"
        invoice_number: (form.invoice_number || "").trim() || null,
        client_id: form.client_id,
        invoice_amount: total,
        tax_amount: Number(form.tax_amount),
        currency: form.currency,
        quantity: Number(form.quantity || 1),
        rate: Number(form.rate || total),
        promotional_discount: Number(form.promotional_discount || 0),
        round_off: Number(form.round_off || 0),
        line_items: items.length > 0
          ? items.map((li) => ({
              description: li.description || "",
              hsn_sac_code: li.hsn_sac_code || null,
              quantity: Number(li.quantity || 1),
              rate: Number(li.rate || li.amount || 0),
              amount: Number(li.amount || li.rate || 0),
              project_id: li.project_id || null,
              change_order_id: li.change_order_id || null,
              kind: li.kind || "custom",
            }))
          : undefined,
      };
      if (!body.line_items) delete body.line_items;
      if (!body.entity_type) delete body.entity_type;
      if (!body.invoice_number) delete body.invoice_number;

      const submit = async (force = false) => {
        const url = force ? "/api/invoices?force=true" : "/api/invoices";
        const { data: created } = await api.post(url, body);
        toast.success(`Invoice ${created.invoice_number} created`);
        // Close the dialog and refresh the list IMMEDIATELY — the FBIL
        // screenshot capture takes ~10-20s and shouldn't block the UI.
        setOpen(false); setForm(emptyForm); load();
        window.dispatchEvent(new Event("invoice-series:refresh"));
        // Fire-and-forget FBIL screenshot download for India-entity non-INR invoices.
        autoDownloadFbilScreenshot(created.id, created.invoice_number, created.entity_type, created.currency);
      };

      try {
        await submit(false);
      } catch (err) {
        const detail = err?.response?.data?.detail;
        if (err?.response?.status === 409 && detail && detail.code === "outstanding_covered") {
          setDupeWarning({ detail, retry: () => submit(true) });
        } else {
          toast.error(detail || "Failed");
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const fetchInrRate = async (chosenSource /* "fbil" | "xe" */) => {
    try {
      const fromCur = form.currency || "USD";
      // FBIL only supports USD→INR — guard the UI accordingly.
      if (chosenSource === "fbil" && fromCur !== "USD") {
        toast.error("FBIL only publishes USD→INR. Use xe.com for other currencies.");
        return;
      }
      const canonical = chosenSource === "fbil" ? "FBIL" : "xe.com";
      // source= locks the scraper (no silent fallback), refresh=1 skips cache.
      const { data } = await api.get(
        `/api/fx/rate?from_ccy=${fromCur}&to_ccy=INR&refresh=1&source=${chosenSource}`
      );
      const dateStr = new Date(data.rate_date).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "2-digit" });
      const converted = (Number(form.invoice_amount || 0) * Number(data.rate)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const note = `${fromCur} to INR Conversion value as on ${dateStr} as per ${canonical} : ${converted} INR (1 ${fromCur} = ${data.rate})`;
      setForm({
        ...form,
        usd_to_inr_note: note,
        fx_rate_to_inr: Number(data.rate),
        // Lock the audit source on the invoice — page 2 of the download PDF
        // will scrape from THIS site so amounts always agree.
        fx_rate_source: canonical,
      });
      toast.success(`Rate fetched: 1 ${fromCur} = ${data.rate} INR (${canonical})`);
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to fetch from ${chosenSource === "fbil" ? "FBIL" : "xe.com"}`);
    }
  };

  const openEdit = (inv) => {
    setEditing(inv);
    setForm({
      ...emptyForm,
      ...inv,
      invoice_date: inv.invoice_date?.slice(0, 10) || "",
      due_date: inv.due_date?.slice(0, 10) || "",
      line_items: Array.isArray(inv.line_items) ? inv.line_items : [],
    });
  };

  const saveEdit = async () => {
    try {
      // Re-derive amount from picker rows if user modified them
      const items = (form.line_items || []).filter((li) => (li.description || "").trim() || Number(li.amount || li.rate || 0) > 0);
      const total = items.length > 0
        ? items.reduce((s, li) => s + Number(li.amount || li.rate || 0), 0)
        : Number(form.invoice_amount);
      const payload = {
        invoice_number: form.invoice_number,
        invoice_date: form.invoice_date,
        due_date: form.due_date,
        invoice_amount: total,
        tax_amount: Number(form.tax_amount),
        currency: form.currency,
        status: form.status,
        notes: form.notes,
        entity_type: form.entity_type,
        invoice_for_label: form.invoice_for_label,
        invoice_for_value: form.invoice_for_value,
        reference: form.reference,
        line_description: form.line_description,
        hsn_sac_code: form.hsn_sac_code,
        quantity: Number(form.quantity || 1),
        rate: Number(form.rate || 0),
        promotional_discount: Number(form.promotional_discount || 0),
        round_off: Number(form.round_off || 0),
        // Do NOT send usd_to_inr_note — the backend re-computes it from
        // final total × latest FBIL rate whenever amount/discount/date change.
        // If we sent the stale UI value, the backend would treat it as an
        // explicit override and skip the refresh.
        //
        // DO send fx_rate_source so the backend knows which scraper the
        // accountant explicitly chose (FBIL vs xe.com); this locks the audit
        // source used for both the regenerated note AND the page-2 screenshot.
        fx_rate_source: form.fx_rate_source || undefined,
        bill_to_name: form.bill_to_name,
        bill_to_address: form.bill_to_address,
        bill_to_contact: form.bill_to_contact,
        bill_to_phone: form.bill_to_phone,
        bill_to_email: form.bill_to_email,
        bill_to_gstin: form.bill_to_gstin,
        line_items: items.length > 0 ? items.map((li) => ({
          description: li.description || "",
          hsn_sac_code: li.hsn_sac_code || null,
          quantity: Number(li.quantity || 1),
          rate: Number(li.rate || li.amount || 0),
          amount: Number(li.amount || li.rate || 0),
          project_id: li.project_id || null,
          change_order_id: li.change_order_id || null,
          kind: li.kind || "custom",
        })) : [],
      };
      // Detect whether an FX-note-affecting field changed so we know to re-capture the FX audit screenshot
      const fxAffecting = (
        (editing.invoice_amount !== total) ||
        (editing.currency !== form.currency) ||
        (editing.invoice_date !== form.invoice_date) ||
        (Number(editing.promotional_discount || 0) !== Number(form.promotional_discount || 0)) ||
        (Number(editing.round_off || 0) !== Number(form.round_off || 0)) ||
        ((editing.fx_rate_source || "") !== (form.fx_rate_source || ""))
      );
      await api.patch(`/api/invoices/${editing.id}`, payload);
      toast.success("Invoice updated");
      setEditing(null); setForm(emptyForm); load();
      window.dispatchEvent(new Event("invoice-series:refresh"));
      // Fire-and-forget FBIL screenshot capture when amount/date/currency changed.
      if (fxAffecting) {
        autoDownloadFbilScreenshot(editing.id, form.invoice_number || editing.invoice_number,
                                    form.entity_type || editing.entity_type,
                                    form.currency || editing.currency);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const remove = async (inv) => {
    if (!window.confirm(`Permanently delete invoice ${inv.invoice_number}? This removes the invoice + all its recorded payments from the database and cannot be undone. The invoice number will become reusable.`)) return;
    try {
      const { data } = await api.delete(`/api/invoices/${inv.id}`);
      if (data?.released_number) {
        toast.success(`Invoice deleted — number ${data.released_number} released back to the series`);
      } else {
        toast.success("Invoice deleted");
      }
      window.dispatchEvent(new Event("invoice-series:refresh"));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const recordPay = async () => {
    // Frontend guardrails: only amount + payment_date are required. Method,
    // reference, notes and INR conversion are optional and can be back-filled
    // later via Payments Received → Edit.
    const amt = Number(pay.amount_received);
    if (!Number.isFinite(amt) || amt <= 0) return toast.error("Enter an amount greater than 0");
    if (!pay.payment_date) return toast.error("Payment date is required");
    try {
      // Strip fx_rate (UI-only helper — backend derives INR from amount_received_inr).
      const { fx_rate: _fxRate, ...rest } = pay;
      const body = {
        ...rest,
        amount_received: amt,
        currency: payOpen.currency,
      };
      if (pay.amount_received_inr !== "" && pay.amount_received_inr !== null)
        body.amount_received_inr = Number(pay.amount_received_inr);
      else delete body.amount_received_inr;
      await api.post(`/api/invoices/${payOpen.id}/payments`, body);
      toast.success("Payment recorded");
      setPayOpen(null); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const startPayPal = async (inv) => {
    try {
      const { data } = await api.post(`/api/paypal/invoices/${inv.id}/order`);
      if (data.approval_url) {
        window.open(data.approval_url, "_blank", "noopener,noreferrer");
        toast.success("PayPal checkout opened in a new tab");
      } else {
        toast.error("No approval URL returned");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "PayPal is not configured. Ask an admin.");
    }
  };

  const copyPublicLink = async (inv) => {
    const url = `${window.location.origin}/pay/${inv.id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Client payment link copied");
    } catch {
      toast.info(url);
    }
  };

  return (
    <div className="space-y-6" data-testid="invoices-page">
      {hasRole(user, "admin") && <DuplicateInvoicesBanner />}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-semibold tracking-tight">Invoices</h1>
          <p className="text-sm text-slate-500 mt-1">Create invoices, record payments, track outstanding.</p>
        </div>
        {hasRole(user, "admin", "accounts") && <SeriesPreviewStrip />}
        {hasRole(user, "admin", "accounts") && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="add-invoice-btn" className="bg-brand hover:bg-brand-hover"><Plus className="w-4 h-4 mr-2" /> New Invoice</Button></DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>New Invoice</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Client *</Label>
                  <Select
                    value={form.client_id}
                    onValueChange={(v) => setForm({ ...form, client_id: v, project_id: "" })}
                  >
                    <SelectTrigger data-testid="invoice-client"><SelectValue placeholder="Select client" /></SelectTrigger>
                    <SelectContent>
                      {clients.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.firm_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Project *</Label>
                  <Select
                    value={form.project_id}
                    onValueChange={(v) => {
                      const p = projects.find((x) => x.id === v);
                      setForm({
                        ...form,
                        project_id: v,
                        currency: p?.currency || form.currency || "USD",
                        rate: p?.project_amount || form.rate,
                        invoice_amount: form.invoice_amount || p?.project_amount || 0,
                        invoice_for_value: p?.project_name || form.invoice_for_value,
                      });
                    }}
                    disabled={!form.client_id}
                  >
                    <SelectTrigger data-testid="invoice-project">
                      <SelectValue placeholder={form.client_id ? "Select project" : "Choose a client first"} />
                    </SelectTrigger>
                    <SelectContent>
                      {projectsForForm.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.project_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Invoice Number <span className="text-slate-400 text-xs">(auto-filled from the series — override if needed)</span></Label><Input data-testid="invoice-number" value={form.invoice_number} onChange={(e) => { setForm({ ...form, invoice_number: e.target.value }); setAutoNumberPrefilled(false); }} placeholder="e.g. TS/26-27/AUG/057" /></div>
                {form.project_id && pendingStagesForProject.length > 0 && (
                  <div className="col-span-2 space-y-1.5">
                    <Label>
                      Link to pending stage <span className="text-slate-400 text-xs">(optional — clears the row from Pending Invoices)</span>
                    </Label>
                    <Select
                      value={form.payment_stage_id || "__none__"}
                      onValueChange={(v) => setForm({ ...form, payment_stage_id: v === "__none__" ? "" : v })}
                    >
                      <SelectTrigger data-testid="invoice-payment-stage"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— Don't link to a stage —</SelectItem>
                        {pendingStagesForProject.map((s) => (
                          <SelectItem key={s.id} value={s.id}>
                            Stage {s.stage_number}: {s.stage_name} · {formatCurrency(s.expected_amount, s.currency)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-1.5"><Label>Currency</Label>
                  <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Invoice Date</Label><Input type="date" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Due Date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
                {/* ── Multi-line-item picker ── */}
                <div className="col-span-2 space-y-2 border-t border-slate-100 pt-3">
                  <div className="flex items-center justify-between">
                    <Label>Line items <span className="text-slate-400 text-xs">(pick projects / change orders, or add custom)</span></Label>
                    <div className="flex items-center gap-2">
                      <Select
                        value=""
                        onValueChange={(v) => {
                          if (!v) return;
                          const p = projects.find((x) => x.id === v);
                          if (!p) return;
                          const li = {
                            description: p.project_name || "",
                            hsn_sac_code: form.currency === "INR" ? "998323" : "",
                            quantity: 1,
                            rate: Number(p.current_project_value || p.project_amount || 0),
                            amount: Number(p.current_project_value || p.project_amount || 0),
                            project_id: p.id,
                            kind: "project",
                          };
                          setForm((f) => ({ ...f, line_items: [...(f.line_items || []), li] }));
                        }}
                      >
                        <SelectTrigger className="w-56 h-8 text-xs" data-testid="add-project-line"><SelectValue placeholder="+ Add project" /></SelectTrigger>
                        <SelectContent>
                          {projectsForForm.filter((p) => !(form.line_items || []).some((li) => li.project_id === p.id)).slice(0, 100).map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.project_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        type="button" variant="outline" size="sm"
                        onClick={() => setForm((f) => ({ ...f, line_items: [...(f.line_items || []), { description: "", quantity: 1, rate: 0, amount: 0, kind: "custom" }] }))}
                        data-testid="add-custom-line"
                      >
                        <Plus className="w-3 h-3 mr-1" /> Custom line
                      </Button>
                    </div>
                  </div>
                  {(form.line_items || []).length > 0 && (
                    <div className="border border-slate-200 rounded-md overflow-hidden">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-50 text-slate-500">
                          <tr>
                            <th className="text-left px-2 py-1.5 w-8">#</th>
                            <th className="text-left px-2 py-1.5">Description</th>
                            <th className="text-left px-2 py-1.5 w-20">SAC</th>
                            <th className="text-right px-2 py-1.5 w-24">Amount</th>
                            <th className="w-8"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {(form.line_items || []).map((li, i) => (
                            <tr key={`li-${i}`} className="border-t border-slate-100" data-testid={`line-row-${i}`}>
                              <td className="px-2 py-1 text-slate-400">{i + 1}</td>
                              <td className="px-2 py-1">
                                <Input value={li.description} className="h-7"
                                  onChange={(e) => setForm((f) => { const list = [...f.line_items]; list[i] = { ...list[i], description: e.target.value }; return { ...f, line_items: list }; })}
                                  data-testid={`line-desc-${i}`}
                                />
                              </td>
                              <td className="px-2 py-1">
                                <Input value={li.hsn_sac_code || ""} className="h-7 font-mono"
                                  onChange={(e) => setForm((f) => { const list = [...f.line_items]; list[i] = { ...list[i], hsn_sac_code: e.target.value }; return { ...f, line_items: list }; })}
                                />
                              </td>
                              <td className="px-2 py-1">
                                <Input type="number" step="0.01" value={li.amount ?? li.rate ?? 0} className="h-7 text-right"
                                  onChange={(e) => setForm((f) => { const list = [...f.line_items]; const n = Number(e.target.value || 0); list[i] = { ...list[i], amount: n, rate: n, quantity: 1 }; return { ...f, line_items: list }; })}
                                  data-testid={`line-amount-${i}`}
                                />
                              </td>
                              <td className="px-2 py-1 text-center">
                                <button type="button" onClick={() => setForm((f) => ({ ...f, line_items: f.line_items.filter((_, j) => j !== i) }))} className="text-slate-400 hover:text-rose-600" data-testid={`line-remove-${i}`}>
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </td>
                            </tr>
                          ))}
                          <tr className="border-t-2 border-slate-200 bg-slate-50 font-medium">
                            <td colSpan={3} className="px-2 py-1.5 text-right text-slate-500">Subtotal</td>
                            <td className="px-2 py-1.5 text-right" data-testid="line-items-subtotal">
                              {form.currency} {(form.line_items || []).reduce((s, li) => s + Number(li.amount || li.rate || 0), 0).toFixed(2)}
                            </td>
                            <td></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                <div className="space-y-1.5"><Label>Amount * <span className="text-slate-400 text-xs">{(form.line_items || []).length > 0 && "(auto from line items)"}</span></Label>
                  <Input
                    type="number" data-testid="invoice-amount"
                    value={(form.line_items || []).length > 0
                      ? (form.line_items || []).reduce((s, li) => s + Number(li.amount || li.rate || 0), 0).toFixed(2)
                      : form.invoice_amount}
                    onChange={(e) => setForm({ ...form, invoice_amount: e.target.value })}
                    disabled={(form.line_items || []).length > 0}
                    title={(form.line_items || []).length > 0 ? "Auto-computed from line items — remove all lines to edit manually" : ""}
                  />
                </div>
                <div className="space-y-1.5"><Label>Tax</Label><Input type="number" value={form.tax_amount} onChange={(e) => setForm({ ...form, tax_amount: e.target.value })} /></div>
                <div className="space-y-1.5">
                  <Label>Template</Label>
                  <Select value={form.entity_type || (form.currency === "INR" ? "india" : "llc")} onValueChange={(v) => setForm({ ...form, entity_type: v })}>
                    <SelectTrigger data-testid="invoice-entity-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="llc">LLC (US) — Bank transfer / ACH</SelectItem>
                      <SelectItem value="india">India — GST / HDFC bank</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 space-y-1.5"><Label>Invoice For — Label</Label><Input value={form.invoice_for_label} placeholder="e.g. Terrazzo Shop Drawings" onChange={(e) => setForm({ ...form, invoice_for_label: e.target.value })} /></div>
                <div className="col-span-2 space-y-1.5"><Label>Invoice For — Value</Label><Input value={form.invoice_for_value} placeholder="e.g. MHH Mont Belvieu MOB" onChange={(e) => setForm({ ...form, invoice_for_value: e.target.value })} /></div>
                <div className="col-span-2 space-y-1.5"><Label>Line Description</Label><Input value={form.line_description} placeholder="e.g. Payment Stage 01: Final 100% of $210" onChange={(e) => setForm({ ...form, line_description: e.target.value })} /></div>
                {(form.entity_type === "india" || (!form.entity_type && form.currency === "INR")) && (
                  <>
                    <div className="space-y-1.5"><Label>HSN/SAC Code</Label><Input value={form.hsn_sac_code || "998323"} onChange={(e) => setForm({ ...form, hsn_sac_code: e.target.value })} /></div>
                    <div className="space-y-1.5"><Label>Quantity</Label><Input type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} /></div>
                    <div className="col-span-2 space-y-1.5">
                      <div className="flex justify-between items-center">
                        <Label>USD→INR Conversion Note</Label>
                        <div className="flex items-center gap-1.5">
                          <Button type="button" size="sm" variant={form.fx_rate_source === "FBIL" ? "default" : "outline"}
                            onClick={() => fetchInrRate("fbil")}
                            disabled={(form.currency || "USD").toUpperCase() !== "USD"}
                            title={(form.currency || "USD").toUpperCase() !== "USD" ? "FBIL publishes USD→INR only" : "Fetch from fbil.org.in"}
                            data-testid="fetch-fbil-rate-btn"
                          >
                            Fetch FBIL rate
                          </Button>
                          <Button type="button" size="sm" variant={form.fx_rate_source === "xe.com" ? "default" : "outline"}
                            onClick={() => fetchInrRate("xe")}
                            data-testid="fetch-xe-rate-btn"
                          >
                            Fetch xe.com rate
                          </Button>
                        </div>
                      </div>
                      <Input value={form.usd_to_inr_note} placeholder="Click a Fetch … rate button to auto-fill" onChange={(e) => setForm({ ...form, usd_to_inr_note: e.target.value })} />
                    </div>
                  </>
                )}
                <div className="space-y-1.5"><Label>Promotional Discount</Label><Input type="number" data-testid="invoice-promotional-discount" value={form.promotional_discount} onChange={(e) => setForm({ ...form, promotional_discount: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Round Off</Label><Input type="number" data-testid="invoice-round-off" value={form.round_off} onChange={(e) => setForm({ ...form, round_off: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Reference</Label><Input value={form.reference} placeholder="NA / PO number" onChange={(e) => setForm({ ...form, reference: e.target.value })} /></div>
                <div className="col-span-2 space-y-1.5"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button className="bg-brand hover:bg-brand-hover" onClick={create} data-testid="save-invoice-btn">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card className="border-slate-200 shadow-none p-4">
        <div className="sticky top-0 z-20 bg-white -m-4 mb-4 px-4 py-3 border-b border-slate-100 flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[180px]">
            <Input placeholder="Search invoice # or project…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="invoices-search" />
          </div>
          <Select value={clientFilter} onValueChange={setClientFilter}>
            <SelectTrigger className="w-40" data-testid="invoice-client-filter"><SelectValue placeholder="Client" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Clients</SelectItem>
              {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.firm_name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={projectFilter} onValueChange={setProjectFilter}>
            <SelectTrigger className="w-40" data-testid="invoice-project-filter"><SelectValue placeholder="Project" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Projects</SelectItem>
              {projectsForFilter.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.project_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="invoice-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {["unpaid", "partially_paid", "paid", "overdue"].map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={entityFilter} onValueChange={setEntityFilter}>
            <SelectTrigger className="w-32" data-testid="invoice-entity-filter"><SelectValue placeholder="Entity" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Entities</SelectItem>
              <SelectItem value="llc">LLC</SelectItem>
              <SelectItem value="india">India</SelectItem>
            </SelectContent>
          </Select>
          <div>
            <Label className="text-[10px] uppercase text-slate-500">Due before</Label>
            <Input type="date" value={dueBefore} onChange={(e) => setDueBefore(e.target.value)} className="w-40" data-testid="invoice-due-filter" />
          </div>
          <Select
            value={sortBy.key === "invoice_date" ? (sortBy.dir === "desc" ? "newest" : "oldest") : "custom"}
            onValueChange={(v) => {
              if (v === "newest") setSortBy({ key: "invoice_date", dir: "desc" });
              else if (v === "oldest") setSortBy({ key: "invoice_date", dir: "asc" });
            }}
          >
            <SelectTrigger className="w-40" data-testid="invoice-sort-order"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest first</SelectItem>
              <SelectItem value="oldest">Oldest first</SelectItem>
              {sortBy.key !== "invoice_date" && <SelectItem value="custom" disabled>Custom · {sortBy.key}</SelectItem>}
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={() => { setClientFilter("all"); setProjectFilter("all"); setStatusFilter("all"); setEntityFilter("all"); setDueBefore(""); setQ(""); }} data-testid="invoice-reset-filters">Reset</Button>
        </div>
        <div className="overflow-x-auto">
        <table className="data-table w-full">
          <thead>
            <tr>
              {[
                ["invoice_number", "Invoice #"],
                ["client_name_snapshot", "Client"],
                ["project_code_snapshot", "Project"],
                ["invoice_date", "Invoice Date"],
                ["due_date", "Due Date"],
                ["outstanding_amount", "Outstanding", "text-right"],
                ["currency", "Currency"],
                ["status", "Status"],
              ].map(([k, label, extra]) => (
                <th key={k} className={extra || ""}>
                  <button
                    type="button"
                    onClick={() => toggleSort(k)}
                    className="inline-flex items-center gap-1 hover:text-slate-900"
                    data-testid={`sort-${k}`}
                  >
                    {label}
                    <span className="text-[10px] text-slate-400">
                      {sortBy.key === k ? (sortBy.dir === "asc" ? "▲" : "▼") : "↕"}
                    </span>
                  </button>
                </th>
              ))}
              <th></th>
            </tr>
          </thead>
          <tbody>
            {[...visibleInvoices].sort((a, b) => {
              const k = sortBy.key;
              let va = a?.[k]; let vb = b?.[k];
              // Numeric columns
              if (k === "outstanding_amount") {
                va = Number(a?.outstanding_amount ?? ((a?.invoice_amount || 0) - (a?.paid_amount || 0)));
                vb = Number(b?.outstanding_amount ?? ((b?.invoice_amount || 0) - (b?.paid_amount || 0)));
              } else {
                va = (va ?? "").toString().toLowerCase();
                vb = (vb ?? "").toString().toLowerCase();
              }
              if (va < vb) return sortBy.dir === "asc" ? -1 : 1;
              if (va > vb) return sortBy.dir === "asc" ? 1 : -1;
              return 0;
            }).map((i) => {
              const client = clientById.get(i.client_id);
              const proj = projects.find((p) => p.id === i.project_id);
              return (
              <tr key={i.id} data-testid={`invoice-row-${i.id}`}>
                <td>
                  <button className="font-mono text-xs hover:underline text-slate-800 inline-flex items-center gap-1"
                          onClick={() => openHistory(i)}
                          data-testid={`open-history-inv-${i.id}`}
                          title="View this invoice's full payment history (received / edited / deleted)">
                    {i.invoice_number}
                    <History className="w-3 h-3 opacity-40" />
                  </button>
                </td>
                <td>
                  {client ? (
                    <button className="text-left hover:underline text-slate-800"
                            onClick={() => navigate(`/clients/${client.id}`)}
                            title={`Go to ${client.firm_name}`}>{client.firm_name}</button>
                  ) : "—"}
                </td>
                <td className="truncate max-w-[240px]">
                  {(() => {
                    const lineItems = i.line_items || [];
                    const pids = Array.from(new Set(lineItems.map((li) => li.project_id).filter(Boolean)));
                    const isMulti = pids.length > 1 || (proj && pids.length === 1 && pids[0] !== proj.id);
                    if (isMulti) {
                      const names = pids.map((pid) => projects.find((pp) => pp.id === pid)?.project_name).filter(Boolean);
                      return (
                        <div className="min-w-0">
                          <div className="text-[10px] inline-flex items-center rounded-full bg-brand-tint text-brand-dark border border-brand/30 px-1.5 py-0.5"
                               title={names.join(", ")}>
                            Consolidated · {pids.length} projects
                          </div>
                          <div className="text-xs text-slate-500 truncate mt-0.5" title={names.join(", ")}>{names.slice(0, 2).join(", ")}{names.length > 2 && ` +${names.length - 2}`}</div>
                        </div>
                      );
                    }
                    return proj ? (
                      <button className="text-left hover:underline text-slate-800"
                              onClick={() => navigate(`/projects/${proj.id}`)}
                              title={`Go to project ${proj.project_name}`}>{proj.project_name}</button>
                    ) : "—";
                  })()}
                </td>
                <td>{i.invoice_date?.slice(0, 10)}</td>
                <td>{i.due_date?.slice(0, 10)}</td>
                <td className="text-right tabular-nums text-amber-700 font-medium">{formatCurrency(i.outstanding_amount, i.currency)}</td>
                <td className="text-slate-500 text-xs">{i.currency}</td>
                <td><StatusBadge status={i.payment_status} /></td>
                <td>
                  <div className="flex gap-1 flex-wrap">
                    <Button size="sm" variant="ghost" onClick={() => openHistory(i)} data-testid={`history-${i.id}`} title="Payment history">
                      <History className="w-3 h-3" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => navigate(`/invoices/${i.id}/print`)} data-testid={`print-${i.id}`} title="Preview / Print"><Printer className="w-3 h-3" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => openEdit(i)} data-testid={`edit-invoice-${i.id}`} title="Edit"><Pencil className="w-3 h-3" /></Button>
                    {hasRole(user, "admin") && (
                      <Button size="sm" variant="ghost" onClick={() => setLineItemsFor(i)} data-testid={`line-items-${i.id}`} title="Edit line items (admin only)">
                        <ListPlus className="w-3 h-3" />
                      </Button>
                    )}
                    {hasRole(user, "admin", "accounts") && i.outstanding_amount > 0 && (
                      <Button size="sm" variant="outline" onClick={() => { setPay({ amount_received: 0, payment_date: new Date().toISOString().slice(0, 10), payment_method: "wire", reference: "", notes: "", amount_received_inr: "", fx_rate: "" }); setPayLastFx(null); setPayOpen(i); }} data-testid={`record-pay-${i.id}`}><DollarSign className="w-3 h-3 mr-1" />Record</Button>
                    )}
                    {hasRole(user, "admin") && (
                      <Button size="sm" variant="ghost" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50" onClick={() => remove(i)} data-testid={`delete-invoice-${i.id}`} title="Delete (admin only)"><Trash2 className="w-3 h-3" /></Button>
                    )}
                  </div>
                </td>
              </tr>
              );
            })}
            {items.length === 0 && <tr><td colSpan={9} className="text-center text-slate-500 py-8">No invoices.</td></tr>}
          </tbody>
        </table>
        </div>
      </Card>

      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) { setEditing(null); setForm(emptyForm); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit Invoice · {editing?.invoice_number}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Invoice Number</Label><Input value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} data-testid="edit-invoice-number" /></div>
            <div className="space-y-1.5"><Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["draft", "ready_to_send", "sent", "cancelled"].map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Invoice Date</Label><Input type="date" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Due Date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Amount <span className="text-slate-400 text-xs">{(form.line_items || []).length > 0 && "(auto from line items)"}</span></Label>
              <Input
                type="number"
                value={(form.line_items || []).length > 0
                  ? (form.line_items || []).reduce((s, li) => s + Number(li.amount || li.rate || 0), 0).toFixed(2)
                  : form.invoice_amount}
                onChange={(e) => setForm({ ...form, invoice_amount: e.target.value })}
                disabled={(form.line_items || []).length > 0}
                data-testid="edit-invoice-amount"
              />
            </div>
            <div className="space-y-1.5"><Label>Tax</Label><Input type="number" value={form.tax_amount} onChange={(e) => setForm({ ...form, tax_amount: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Currency</Label>
              <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Template</Label>
              <Select value={form.entity_type || (form.currency === "INR" ? "india" : "llc")} onValueChange={(v) => setForm({ ...form, entity_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="llc">LLC (US)</SelectItem>
                  <SelectItem value="india">India (GST)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-1.5"><Label>Invoice For — Label</Label><Input value={form.invoice_for_label || ""} onChange={(e) => setForm({ ...form, invoice_for_label: e.target.value })} /></div>
            <div className="col-span-2 space-y-1.5"><Label>Invoice For — Value</Label><Input value={form.invoice_for_value || ""} onChange={(e) => setForm({ ...form, invoice_for_value: e.target.value })} /></div>
            <div className="col-span-2 space-y-1.5"><Label>Line Description</Label><Input value={form.line_description || ""} onChange={(e) => setForm({ ...form, line_description: e.target.value })} /></div>
            {(form.entity_type === "india" || form.currency === "INR") && (
              <>
                <div className="space-y-1.5"><Label>HSN/SAC Code</Label><Input value={form.hsn_sac_code || ""} onChange={(e) => setForm({ ...form, hsn_sac_code: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Quantity</Label><Input type="number" value={form.quantity || 1} onChange={(e) => setForm({ ...form, quantity: e.target.value })} /></div>
                <div className="col-span-2 space-y-1.5">
                  <div className="flex justify-between items-center">
                    <Label>USD→INR Note</Label>
                    <div className="flex items-center gap-1.5">
                      <Button type="button" size="sm" variant={form.fx_rate_source === "FBIL" ? "default" : "outline"}
                        onClick={() => fetchInrRate("fbil")}
                        disabled={(form.currency || "USD").toUpperCase() !== "USD"}
                        title={(form.currency || "USD").toUpperCase() !== "USD" ? "FBIL publishes USD→INR only" : "Fetch from fbil.org.in"}
                        data-testid="edit-fetch-fbil-rate-btn"
                      >
                        Fetch FBIL rate
                      </Button>
                      <Button type="button" size="sm" variant={form.fx_rate_source === "xe.com" ? "default" : "outline"}
                        onClick={() => fetchInrRate("xe")}
                        data-testid="edit-fetch-xe-rate-btn"
                      >
                        Fetch xe.com rate
                      </Button>
                    </div>
                  </div>
                  <Input data-testid="edit-usd-inr-note" value={form.usd_to_inr_note || ""} onChange={(e) => setForm({ ...form, usd_to_inr_note: e.target.value })} />
                </div>
              </>
            )}
            <div className="space-y-1.5"><Label>Promotional Discount</Label><Input type="number" data-testid="edit-promotional-discount" value={form.promotional_discount || 0} onChange={(e) => setForm({ ...form, promotional_discount: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Round Off</Label><Input type="number" data-testid="edit-round-off" value={form.round_off || 0} onChange={(e) => setForm({ ...form, round_off: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Reference</Label><Input value={form.reference || ""} onChange={(e) => setForm({ ...form, reference: e.target.value })} /></div>
            <div className="col-span-2 border-t border-slate-100 pt-3 text-xs uppercase tracking-widest text-slate-500 font-semibold">Bill To (override snapshot)</div>
            <div className="space-y-1.5"><Label>Bill-to Name</Label><Input value={form.bill_to_name || ""} onChange={(e) => setForm({ ...form, bill_to_name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Contact Person</Label><Input value={form.bill_to_contact || ""} onChange={(e) => setForm({ ...form, bill_to_contact: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Phone</Label><Input value={form.bill_to_phone || ""} onChange={(e) => setForm({ ...form, bill_to_phone: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input value={form.bill_to_email || ""} onChange={(e) => setForm({ ...form, bill_to_email: e.target.value })} /></div>
            <div className="col-span-2 space-y-1.5"><Label>Address</Label><Input value={form.bill_to_address || ""} onChange={(e) => setForm({ ...form, bill_to_address: e.target.value })} /></div>
            {(form.entity_type === "india" || form.currency === "INR") && (
              <div className="col-span-2 space-y-1.5"><Label>Client GSTIN</Label><Input value={form.bill_to_gstin || ""} onChange={(e) => setForm({ ...form, bill_to_gstin: e.target.value })} /></div>
            )}
            {/* Multi-line items on the Edit dialog — same picker as New Invoice. */}
            <div className="col-span-2 space-y-2 border-t border-slate-100 pt-3">
              <div className="flex items-center justify-between">
                <Label>Line items <span className="text-slate-400 text-xs">(pick projects / change orders, or add custom)</span></Label>
                <div className="flex items-center gap-2">
                  <Select
                    value=""
                    onValueChange={(v) => {
                      if (!v) return;
                      const p = projects.find((x) => x.id === v);
                      if (!p) return;
                      const li = {
                        description: p.project_name || "",
                        hsn_sac_code: form.currency === "INR" ? "998323" : "",
                        quantity: 1, rate: Number(p.current_project_value || 0), amount: Number(p.current_project_value || 0),
                        project_id: p.id, kind: "project",
                      };
                      setForm((f) => ({ ...f, line_items: [...(f.line_items || []), li] }));
                    }}
                  >
                    <SelectTrigger className="w-56 h-8 text-xs" data-testid="edit-add-project-line"><SelectValue placeholder="+ Add project" /></SelectTrigger>
                    <SelectContent>
                      {projectsForForm.filter((p) => !(form.line_items || []).some((li) => li.project_id === p.id)).slice(0, 100).map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.project_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button type="button" variant="outline" size="sm"
                    onClick={() => setForm((f) => ({ ...f, line_items: [...(f.line_items || []), { description: "", quantity: 1, rate: 0, amount: 0, kind: "custom" }] }))}
                    data-testid="edit-add-custom-line"
                  ><Plus className="w-3 h-3 mr-1" /> Custom line</Button>
                </div>
              </div>
              {(form.line_items || []).length > 0 && (
                <div className="border border-slate-200 rounded-md overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-slate-500"><tr>
                      <th className="text-left px-2 py-1.5 w-8">#</th>
                      <th className="text-left px-2 py-1.5">Description</th>
                      <th className="text-left px-2 py-1.5 w-20">SAC</th>
                      <th className="text-right px-2 py-1.5 w-24">Amount</th>
                      <th className="w-8"></th>
                    </tr></thead>
                    <tbody>
                      {(form.line_items || []).map((li, i) => (
                        <tr key={`eli-${i}`} className="border-t border-slate-100" data-testid={`edit-line-row-${i}`}>
                          <td className="px-2 py-1 text-slate-400">{i + 1}</td>
                          <td className="px-2 py-1"><Input value={li.description} className="h-7"
                            onChange={(e) => setForm((f) => { const list = [...f.line_items]; list[i] = { ...list[i], description: e.target.value }; return { ...f, line_items: list }; })}
                            data-testid={`edit-line-desc-${i}`} />
                          </td>
                          <td className="px-2 py-1"><Input value={li.hsn_sac_code || ""} className="h-7 font-mono"
                            onChange={(e) => setForm((f) => { const list = [...f.line_items]; list[i] = { ...list[i], hsn_sac_code: e.target.value }; return { ...f, line_items: list }; })} />
                          </td>
                          <td className="px-2 py-1"><Input type="number" step="0.01" value={li.amount ?? li.rate ?? 0} className="h-7 text-right"
                            onChange={(e) => setForm((f) => { const list = [...f.line_items]; const n = Number(e.target.value || 0); list[i] = { ...list[i], amount: n, rate: n, quantity: 1 }; return { ...f, line_items: list }; })}
                            data-testid={`edit-line-amount-${i}`} />
                          </td>
                          <td className="px-2 py-1 text-center">
                            <button type="button" onClick={() => setForm((f) => ({ ...f, line_items: f.line_items.filter((_, j) => j !== i) }))} className="text-slate-400 hover:text-rose-600" data-testid={`edit-line-remove-${i}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </td>
                        </tr>
                      ))}
                      <tr className="border-t-2 border-slate-200 bg-slate-50 font-medium">
                        <td colSpan={3} className="px-2 py-1.5 text-right text-slate-500">Subtotal</td>
                        <td className="px-2 py-1.5 text-right" data-testid="edit-line-items-subtotal">
                          {form.currency} {(form.line_items || []).reduce((s, li) => s + Number(li.amount || li.rate || 0), 0).toFixed(2)}
                        </td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="col-span-2 space-y-1.5"><Label>Notes</Label><Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditing(null); setForm(emptyForm); }}>Cancel</Button>
            <Button className="bg-brand hover:bg-brand-hover" onClick={saveEdit} data-testid="save-edit-invoice">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!payOpen} onOpenChange={(v) => !v && setPayOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Record Payment · {payOpen?.invoice_number}</DialogTitle></DialogHeader>
          <div className="text-sm text-slate-500 mb-2">Outstanding: <span className="font-semibold text-amber-700">{payOpen && formatCurrency(payOpen.outstanding_amount, payOpen.currency)}</span></div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Amount * <span className="text-slate-400 text-xs">({payOpen?.currency || ""})</span></Label><Input data-testid="pay-amount" type="number" value={pay.amount_received} onChange={(e) => setPayAmount(e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Date *</Label><Input type="date" value={pay.payment_date} onChange={(e) => setPay({ ...pay, payment_date: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Method <span className="text-slate-400 text-xs">(optional)</span></Label>
              <Select value={pay.payment_method} onValueChange={(v) => setPay({ ...pay, payment_method: v })}>
                <SelectTrigger data-testid="record-pay-method"><SelectValue /></SelectTrigger>
                <SelectContent>{[
                  ["wire_ach", "Wire / ACH"],
                  ["cc_stripe", "CC (Stripe)"],
                  ["paypal", "PayPal"],
                  ["payoneer", "Payoneer"],
                  ["wire_in", "Wire IN"],
                  ["others", "Others"],
                ].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Reference <span className="text-slate-400 text-xs">(optional)</span></Label><Input value={pay.reference} onChange={(e) => setPay({ ...pay, reference: e.target.value })} /></div>
            {payOpen?.currency && payOpen.currency !== "INR" && (
              <div className="col-span-2 space-y-2 rounded-md border border-slate-200 bg-slate-50/50 p-3">
                <div className="text-xs font-medium text-slate-600 flex items-center gap-2">
                  <span>{payOpen.currency} → INR conversion</span>
                  <span className="text-[10px] text-slate-400 font-normal">optional — add now or later</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Conversion rate</Label>
                    {rateHistory.length > 0 && (
                      <div className="flex flex-wrap items-center gap-1.5 pb-1" data-testid="rate-history-chips">
                        <span className="text-[10px] uppercase tracking-wide text-slate-400">Recent:</span>
                        {rateHistory.map((h, i) => (
                          <button
                            type="button"
                            key={`rate-chip-${i}`}
                            onClick={() => setPayRate(String(h.rate))}
                            className="group inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-brand/30 bg-brand-tint text-brand-dark hover:bg-brand hover:text-white text-[11px] font-medium transition"
                            title={`Reuse rate ${h.rate} from ${h.date || "prior payment"}`}
                            data-testid={`rate-chip-${i}`}
                          >
                            {h.rate}
                            {h.date && <span className="text-[9px] font-normal opacity-70 group-hover:opacity-100">· {h.date}</span>}
                          </button>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500 tabular-nums whitespace-nowrap">1 {payOpen.currency} =</span>
                      <Input
                        data-testid="pay-fx-rate"
                        type="number"
                        step="0.000001"
                        value={pay.fx_rate}
                        onChange={(e) => setPayRate(e.target.value)}
                        placeholder="e.g. 83.50"
                        className="flex-1"
                      />
                      <span className="text-xs text-slate-500 whitespace-nowrap">INR</span>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Actual INR received</Label>
                    <Input
                      data-testid="pay-inr-amount"
                      type="number"
                      step="0.01"
                      value={pay.amount_received_inr}
                      onChange={(e) => setPayInr(e.target.value)}
                      placeholder="exact ₹ credited to bank"
                    />
                  </div>
                </div>
                <div className="text-[11px] text-slate-500">
                  Used for BD incentive calculation. Type either the rate or the actual INR credited to the bank — the other auto-fills. Safe to leave blank now and add it later from Payments Received when the withdrawal actually happens.
                </div>
              </div>
            )}
            <div className="col-span-2 space-y-1.5"><Label>Notes</Label><Textarea value={pay.notes} onChange={(e) => setPay({ ...pay, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(null)}>Cancel</Button>
            <Button className="bg-brand hover:bg-brand-hover" onClick={recordPay} data-testid="save-payment-btn">Record Payment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment history timeline */}
      <Dialog open={!!historyFor} onOpenChange={(v) => { if (!v) { setHistoryFor(null); setHistory(null); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Payment History · {historyFor?.invoice_number}</DialogTitle>
          </DialogHeader>
          {!history ? (
            <div className="text-slate-500 py-6 text-center">Loading…</div>
          ) : history.timeline.length === 0 ? (
            <div className="text-slate-500 py-6 text-center">No payments recorded yet.</div>
          ) : (
            <div className="space-y-3" data-testid="payment-history-timeline">
              <div className="text-xs text-slate-500 flex gap-4 border-b pb-2">
                <span>Received: <b className="text-emerald-700">{formatCurrency(history.amount_received || 0, historyFor?.currency)}</b></span>
                <span>Outstanding: <b className="text-amber-700">{formatCurrency(history.outstanding || 0, historyFor?.currency)}</b></span>
                <span className="ml-auto text-slate-400">{history.timeline.length} event{history.timeline.length === 1 ? "" : "s"}</span>
              </div>
              <ol className="relative border-l-2 border-slate-200 ml-2 space-y-4 pl-4 pt-2">
                {history.timeline.map((ev, idx) => {
                  const iconTone =
                    ev.type === "received" && !ev.is_deleted ? "bg-emerald-500" :
                    ev.type === "edited" ? "bg-amber-500" :
                    ev.type === "deleted" ? "bg-rose-500" :
                    "bg-slate-300";
                  return (
                    <li key={ev.at || ev.txn_id || `event-${idx}`} className="relative" data-testid={`history-event-${idx}`}>
                      <span className={`absolute -left-[22px] top-1.5 w-3 h-3 rounded-full ${iconTone} border-2 border-white`}></span>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="text-sm font-medium capitalize">
                            {ev.type === "received" && (ev.is_deleted ? "Payment received (later deleted)" : "Payment received")}
                            {ev.type === "edited" && "Payment edited"}
                            {ev.type === "deleted" && "Payment deleted"}
                          </div>
                          {ev.type === "received" && (
                            <div className="text-xs text-slate-500 mt-0.5">
                              <span className="text-emerald-700 font-medium">{formatCurrency(ev.amount, ev.currency)}</span>
                              {ev.payment_method && <> · {ev.payment_method.replace(/_/g, " ")}</>}
                              {ev.reference && <> · ref {ev.reference}</>}
                              {ev.payment_date && <> · dated {ev.payment_date.slice(0, 10)}</>}
                              {ev.txn_id && !ev.is_deleted && (
                                <>
                                  {" · "}
                                  <button
                                    type="button"
                                    className="text-brand hover:text-brand-hover font-medium underline underline-offset-2"
                                    onClick={() => navigate(`/payments?edit=${ev.txn_id}`)}
                                    data-testid={`history-edit-${ev.txn_id}`}
                                    title="Open this payment in Payments Received to edit amount, date, method, INR conversion, etc."
                                  >
                                    Edit
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                          {ev.type === "edited" && (
                            <div className="text-xs text-slate-500 mt-0.5 space-y-0.5">
                              {(() => {
                                const before = ev.changes?.before || {};
                                const after = ev.changes?.after || (Object.keys(before).length === 0 ? ev.changes : {});
                                const keys = Array.from(new Set([...Object.keys(before), ...Object.keys(after || {})]))
                                  .filter((k) => !["updated_at", "last_edited_by", "last_edited_by_snapshot"].includes(k));
                                if (keys.length === 0) return <div className="italic">No trackable field changes</div>;
                                return keys.map((k) => (
                                  <div key={k}>
                                    <b className="text-slate-700">{k.replace(/_/g, " ")}</b>:{" "}
                                    <span className="line-through text-slate-400">{String(before[k] ?? "—")}</span>
                                    <span className="mx-1">→</span>
                                    <span className="text-slate-800 font-medium">{String(after[k] ?? "—")}</span>
                                  </div>
                                ));
                              })()}
                            </div>
                          )}
                          {ev.type === "deleted" && (
                            <div className="text-xs text-slate-500 mt-0.5">
                              Removed <span className="text-rose-700 font-medium">{formatCurrency(ev.amount, ev.currency)}</span> — invoice balance restored.
                            </div>
                          )}
                        </div>
                        <div className="text-[11px] text-slate-400 whitespace-nowrap text-right">
                          {ev.at && new Date(ev.at).toLocaleString()}
                          {ev.actor && <div>by {ev.actor}</div>}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ol>
              <div className="text-[11px] text-slate-400 pt-2 border-t">
                Timeline preserves every correction — deleted rows never fully disappear, so any earlier accounting mistake is traceable.
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {lineItemsFor && (
        <LineItemsEditor
          invoice={lineItemsFor}
          open={!!lineItemsFor}
          onOpenChange={(v) => !v && setLineItemsFor(null)}
          onSaved={() => { setLineItemsFor(null); load(); }}
        />
      )}

      {dupeWarning && (
        <Dialog open={!!dupeWarning} onOpenChange={(v) => !v && setDupeWarning(null)}>
          <DialogContent className="max-w-lg" data-testid="dupe-invoice-warning-dialog">
            <DialogHeader>
              <DialogTitle className="text-amber-800">Over-invoicing blocked</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <p className="text-slate-700">{dupeWarning.detail.message}</p>
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 space-y-3">
                {(dupeWarning.detail.projects || []).map((p) => {
                  const headroom = Number(p.remaining_headroom ?? Math.max((p.project_value || 0) - (p.already_invoiced || 0), 0));
                  const overBy = Math.max(Number(p.new_amount || 0) - headroom, 0);
                  return (
                    <div key={p.project_id} className="text-xs" data-testid={`dupe-warning-project-${p.project_id}`}>
                      <div className="font-mono font-semibold text-amber-900">
                        {p.project_code || "—"} {p.project_name ? `· ${p.project_name}` : ""}
                      </div>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 mt-1 text-slate-700">
                        <div>Project value: <span className="font-medium">{p.currency || ""} {Number(p.project_value).toLocaleString()}</span></div>
                        <div>Already invoiced: <span className="font-medium">{p.currency || ""} {Number(p.already_invoiced).toLocaleString()}</span></div>
                        <div>This invoice adds: <span className="font-medium">{p.currency || ""} {Number(p.new_amount).toLocaleString()}</span></div>
                        <div>
                          <span className={headroom <= 0 ? "text-rose-700" : "text-emerald-700"}>Remaining headroom:</span>{" "}
                          <span className={`font-semibold ${headroom <= 0 ? "text-rose-700" : "text-emerald-700"}`}>
                            {p.currency || ""} {headroom.toLocaleString()}
                          </span>
                        </div>
                      </div>
                      {overBy > 0 && (
                        <div className="mt-1 text-rose-700 font-medium" data-testid={`dupe-warning-over-${p.project_id}`}>
                          Would over-bill by {p.currency || ""} {overBy.toLocaleString()} — reduce the amount by at least this much or remove this project from the invoice.
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-slate-500">
                Proceed only if you're intentionally billing beyond the current project value
                (e.g. approved change orders not yet reflected in the project total).
              </p>
            </div>
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => setDupeWarning(null)}
                data-testid="dupe-warning-cancel"
              >
                Cancel
              </Button>
              <Button
                className="bg-brand hover:bg-brand-hover"
                data-testid="dupe-warning-proceed"
                onClick={async () => {
                  const retry = dupeWarning.retry;
                  setDupeWarning(null);
                  try { await retry(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                }}
              >
                Create anyway
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
